import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { isSupabaseError } from "../../../../constants/supabaseErrors";
import { ProviderDatabase } from "../../../../types";
import usePostProvider from "../../api/usePostProvider";
import useUpdateProvider from "../../api/useUpdateProvider";
import { ProviderTag, ProviderWithDetails } from "../../types";

const formSchema = z.object({
  name: z.string().min(1, 'Ce champ est obligatoire'),
  description: z.string(),
  address: z.string().nullable().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface UseAddUpdateProviderFormProps {
  provider?: ProviderDatabase | ProviderWithDetails;
  onClose: () => void;
  afterSuccess?: (provider?: ProviderDatabase) => void;
}

export default function useAddUpdateProviderForm({
  provider,
  onClose,
  afterSuccess,
}: UseAddUpdateProviderFormProps) {
  const { mutateAsync: postProvider, isPending: isPosting } = usePostProvider();
  const { mutateAsync: updateProvider, isPending: isUpdating } = useUpdateProvider();

  const [serverError, setServerError] = useState<string | null>(null);

  const initialTags: ProviderTag[] =
    provider && 'tags' in provider && Array.isArray(provider.tags)
      ? provider.tags
      : [];

  const [selectedTags, setSelectedTags] = useState<ProviderTag[]>(initialTags);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: provider?.name ?? "",
      description: provider?.description ?? "",
      address: provider?.address ?? "",
    },
  });

  function resolveTagIds(): string[] {
    return selectedTags.map((t) => t.id);
  }

  async function handleSubmit(data: FormValues) {
    setServerError(null);

    try {
      const tagIds = await resolveTagIds();

      if (provider?.id) {
        const result = await updateProvider({
          providerId: provider.id,
          command: {
            ...data,
            tag_ids: tagIds,
          },
        });
        afterSuccess?.(result ?? undefined);
      } else {
        const result = await postProvider({
          command: {
            ...data,
            tag_ids: tagIds,
          },
        });
        afterSuccess?.(result ?? undefined);
      }
      onClose();
    } catch (errors: unknown) {
      if (isSupabaseError(errors)) {
        setServerError(errors.message);
        return;
      }

      setServerError('Une erreur est survenue.');
    }
  }

  return {
    form,
    onSubmit: form.handleSubmit(handleSubmit),
    isSubmitting: isUpdating || isPosting,
    serverError,
    selectedTags,
    setSelectedTags,
  };
}
